import { Flex } from '@gmg/gmg-react-components';
import { FunctionComponent } from 'react';
import { useTranslation } from 'react-i18next';
import { ModuleLicense } from 'src/viewModels/Organization';
import styled from 'styled-components';
import ValidityCell from './ValidityCell';

export interface ProductRowProps {
    product: ModuleLicense;
};

const ProductRow: FunctionComponent<ProductRowProps> = ({ product }) => {
    const { t } = useTranslation();
    const lng = useTranslation().i18n.language;

    const getUsageDescription = (currentProduct: ModuleLicense) => {
        switch (currentProduct.type) {
            case 'counterBased':
                return <Flex flexDirection='column' gap='4px'>
                    <div aria-label='product usage'>
                        {t('ProductManagementTable.usage_counterbased_primary_desc', {
                            defaultValue: '{{remaining}} cards available (of {{total}} total)',
                            remaining: currentProduct.counters?.availableNumber.toLocaleString(lng, {
                                useGrouping: true,
                            }),
                            total: currentProduct.counters?.totalNumberInContracts.toLocaleString(lng, {
                                useGrouping: true,
                            }),
                        })}
                    </div>
                    <div className="px10_400_weak" aria-label='product usage secondary'>
                        {t('ProductManagementTable.usage_counterbased_secondary_desc', {
                            defaultValue: 'Used: {{used}}',
                            used: currentProduct.counters?.assignedNumber.toLocaleString(lng, {
                                useGrouping: true,
                            }),
                        })}
                    </div>
                </Flex>;
            case 'unlimited':
                return <Flex flexDirection='column' gap='4px'>
                    <div aria-label='product usage'>
                        {t('ProductManagementTable.usage_unlimited_primary_desc', {
                            defaultValue: 'Unlimited seats',
                        })}
                    </div>
                    <div className="px10_400_weak" aria-label='product usage secondary'>
                        {t('ProductManagementTable.usage_unlimited_secondary_desc', {
                            defaultValue: 'Assigned: {{assigned}}',
                            assigned: currentProduct.numberOfAssignedSeats.toLocaleString(lng, {
                                useGrouping: true,
                            }),
                        })}
                    </div>
                </Flex>;
            case 'seatBased':
                return <Flex flexDirection='column' gap='4px'>
                    <div aria-label='product usage'>
                        {t('ProductManagementTable.usage_seatbased_primary_desc', {
                            defaultValue: '{{remaining}} cards available (of {{total}} total)',
                            remaining: currentProduct.effectiveNumberOfAvailableSeats > 0 ? currentProduct.effectiveNumberOfAvailableSeats
                                .toLocaleString(lng,
                                    { useGrouping: true },
                                ) : 0,
                            total: currentProduct.totalNumberOfSeatsInContracts.toLocaleString(lng, {
                                useGrouping: true,
                            }),
                        })}
                    </div>
                    <div className="px10_400_weak" aria-label='product usage secondary'>
                        {t('ProductManagementTable.usage_seatbased_secondary_desc', {
                            defaultValue: 'Assigned: {{assigned}}',
                            assigned: currentProduct.numberOfAssignedSeats.toLocaleString(lng, {
                                useGrouping: true,
                            }),
                        })}
                    </div>
                </Flex>;
        }
    };

    return (
        <Row
            key={product.name}
            data-testid="row"
            aria-label={`product inforamtion for: ${product.name}`}
        >
            <CenteredFlexCell aria-label='product name' className="px12_400_normal">
                {product.name}
            </CenteredFlexCell>
            <CenteredFlexCell aria-label='product type'>
                <span className="px12_400_normal">
                    {t('ProductManagementTable.type_subscription_title', 'Subscription')}
                </span>
            </CenteredFlexCell>
            <Flex gap='4px' flexDirection='column' justifyContent='center'>
                <Flex gap='6px'>
                    <span className="px12_400_normal">
                        {getUsageDescription(product)}
                    </span>
                </Flex>
            </Flex>
            <CenteredFlexCell aria-label='product expiration' className="px12_400_normal">
                <ValidityCell contractInfo={product.contractStatus.nearestExpiringContract} />
            </CenteredFlexCell>
        </Row>
    );
};

const Row = styled.div`
    display: grid;
    grid-template-columns: repeat(7, var(--product-table-grid-column-width));
    height: var(--table-row-height);
    min-height: var(--table-row-height);
    align-items: stretch;
    padding-left: 20px;
    background-color: ${props => props.theme.colors.backgroundPaper};
    border-bottom: 1px solid transparent;
    border-top: 1px solid transparent;
    z-index: 0;
`;

const CenteredFlexCell = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
`;

export default ProductRow;
